/**
 * „Zgłoś użytkownika" — the mobile app's report sheet as a dialog: what happened, in the trainer's
 * words, sent to the support team. The conversation is untouched (`chat.report.body` says so). The
 * server allows five reports an hour, and a 429 says that rather than „try again".
 */

import { Dialog } from '@base-ui/react/dialog';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import surface from '~/shared/ui/dialogSurface.module.css';
import form from '~/shared/ui/formDialog.module.css';

import { MAX_REPORT_REASON, reportConversation } from '../lib/reportConversation';

import { CloseGlyph } from './ChatGlyphs';
import styles from './chat.module.css';

export function ReportDialog({
  conversationId,
  open,
  onClose,
  onReported,
}: {
  readonly conversationId: number;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onReported: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const fieldId = useId();

  const submit = () => {
    const text = reason.trim();
    if (text === '' || sending) return;
    setSending(true);
    setRefusal(null);
    void reportConversation(conversationId, text).then((outcome) => {
      setSending(false);
      if (outcome === 'sent') {
        setReason('');
        onReported();
        onClose();
        return;
      }
      setRefusal(outcome === 'rateLimited' ? t('chat.report.errorRateLimited') : t('chat.report.error'));
    });
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={surface.backdrop} />
        <Dialog.Popup className={`${String(surface.popup)} ${String(form.dialog)}`}>
          <div className={form.dialogHead}>
            <Dialog.Title className={form.dialogTitle}>{t('chat.report.title')}</Dialog.Title>
            <Dialog.Close className={form.close} aria-label={t('chat.report.cancel')}>
              <CloseGlyph size={16} />
            </Dialog.Close>
          </div>
          <Dialog.Description className={form.questionBody}>{t('chat.report.body')}</Dialog.Description>
          <div className={form.field}>
            <label className={form.fieldLabel} htmlFor={fieldId}>
              {t('chat.report.label')}
            </label>
            <textarea
              id={fieldId}
              className={`${String(form.input)} ${styles.reportField}`}
              value={reason}
              maxLength={MAX_REPORT_REASON}
              rows={4}
              placeholder={t('chat.report.placeholder')}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </div>
          {refusal === null ? null : (
            <p className={form.refusal} role="alert">
              {refusal}
            </p>
          )}
          <div className={form.dialogActs}>
            <button type="button" className={surface.ghost} onClick={onClose}>
              {t('chat.report.cancel')}
            </button>
            <button
              type="button"
              className={surface.primary}
              aria-disabled={reason.trim() === '' || sending ? true : undefined}
              aria-busy={sending}
              onClick={submit}
            >
              {sending ? t('chat.report.submitBusy') : t('chat.report.submit')}
            </button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
